/**
 * Freedom Fuel — Cart Drawer
 *
 * Hydrates:
 *   1. [data-ff-shipbar]      — Free shipping progress (msg, togo, fill, ticks)
 *   2. [data-ff-vote-commit]  — Hero carousel with per-hero vote allocation
 *
 * Allocations persist to:
 *   - localStorage (immediate)
 *   - cart.attributes.ff_hero_allocations (JSON map {heroId: count}) via /cart/update.js
 *
 * Re-initialises automatically on cart:updated, dialog:open, and any insertion
 * of the [data-ff-vote-commit] shell (cart drawer SRA morph).
 */

const VOTING_API = 'https://voting.freedomfuel.us';
const CART_ATTR_ALLOCATIONS = 'ff_hero_allocations';
const LS_KEY_ALLOCATIONS    = 'ff_hero_allocations';

const HERO_ACCENTS = ['#3A3326', '#2E1F1B', '#1B2A3A', '#2A1F2E', '#3A2820', '#1F2E2A'];

/* ── State ── */
const state = {
  heroes: [],          // fetched once
  fetched: false,
  allocations: {},     // { heroId: count }
  earnedVotes: 0,      // total votes earnable from current cart
  pageIndex: 0,        // carousel page index for pagination dots
};

let eventsBound = false;

/* ── Boot ── */
(function init() {
  restoreAllocations();
  hydrate();

  document.addEventListener('cart:updated', () => setTimeout(hydrate, 50));
  document.addEventListener('dialog:open',  () => setTimeout(hydrate, 50));

  new MutationObserver(records => {
    for (const r of records) {
      for (const node of r.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('[data-ff-vote-commit], [data-ff-shipbar], [data-ff-puckbar]') ||
            node.querySelector?.('[data-ff-vote-commit], [data-ff-shipbar], [data-ff-puckbar]')) {
          setTimeout(hydrate, 30);
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  let polls = 0;
  const poll = setInterval(() => {
    if (hydrate() || ++polls > 12) clearInterval(poll);
  }, 250);
})();

/* ── Hydration ── */
function hydrate() {
  const vcRoots = [...document.querySelectorAll('[data-ff-vote-commit]')].filter(el => document.body.contains(el));
  const sbRoots = [...document.querySelectorAll('[data-ff-shipbar]')].filter(el => document.body.contains(el));
  const pbRoots = [...document.querySelectorAll('[data-ff-puckbar]')].filter(el => document.body.contains(el));
  if (!vcRoots.length && !sbRoots.length && !pbRoots.length) return false;

  bindEvents();

  if (vcRoots.length) {
    state.earnedVotes = Number(vcRoots[0].dataset.orderVotes || 0);
    clampAllocations();

    if (!state.fetched) {
      setCarousels(loadingHTML());
      fetchHeroes().then(() => renderCarousels());
    } else {
      renderCarousels();
    }
    renderAllocPanel();
  }

  if (sbRoots.length) updateShipBars();
  if (pbRoots.length) updatePuckBars();

  return true;
}

/* ── Events (delegated, once) ── */
function bindEvents() {
  if (eventsBound) return;
  eventsBound = true;

  document.body.addEventListener('click', e => {
    // Open cart drawer + focus a specific hero card (used by hero nominee "Vote" CTAs)
    const voteCta = e.target.closest('[data-ff-vote-hero-id]');
    if (voteCta) {
      const heroId = voteCta.getAttribute('data-ff-vote-hero-id');
      if (heroId) {
        e.preventDefault();
        focusHero(heroId);
        return;
      }
    }

    // Carousel nav
    const nav = e.target.closest('[data-ff-nav]');
    if (nav) {
      const wrap = nav.closest('.ff-vc-carousel-wrap');
      const car  = wrap?.querySelector('[data-ff-carousel]');
      if (!car) return;
      const step = car.querySelector('.ff-hero-card')?.offsetWidth || 212;
      car.scrollBy({ left: nav.dataset.ffNav === 'next' ? step + 12 : -(step + 12), behavior: 'smooth' });
      return;
    }

    // Hero vote stepper
    const stepBtn = e.target.closest('.ff-vote-stepper button');
    if (stepBtn) {
      const stepper = stepBtn.closest('.ff-vote-stepper');
      const heroId  = stepper?.dataset.hid;
      if (!heroId) return;
      const act = stepBtn.dataset.act;
      if (act === 'inc') incAllocation(heroId);
      else if (act === 'dec') decAllocation(heroId);
      return;
    }

    // Reset
    if (e.target.closest('[data-ff-alloc-reset]')) {
      state.allocations = {};
      persistAllocations();
      renderCarousels();
      renderAllocPanel();
      return;
    }

    // Split equally across all heroes
    if (e.target.closest('[data-ff-alloc-split]')) {
      splitEqually();
      return;
    }
  });

  // Track scroll to update pagination dots
  document.body.addEventListener('scroll', e => {
    const car = e.target.closest?.('[data-ff-carousel]');
    if (!car) return;
    updatePagination(car);
  }, true);
}

/* ── Fetch heroes ── */
async function fetchHeroes() {
  try {
    const res  = await fetch(`${VOTING_API}/api/storefront/heroes`, { mode: 'cors' });
    const json = await res.json();
    state.heroes  = json.heroes || [];
    state.fetched = true;
  } catch (err) {
    console.warn('[ffvc] fetchHeroes failed', err);
    state.heroes  = [];
    state.fetched = true;
    setCarousels(emptyHTML('Could not load heroes. Please try again later.'));
  }
}

/* ── Render heroes ── */
function renderCarousels() {
  if (!state.heroes.length) {
    setCarousels(emptyHTML('No heroes available yet.'));
    return;
  }

  setCarousels(state.heroes.map((h, i) => renderCard(h, i)).join(''));
  document.querySelectorAll('[data-ff-carousel]').forEach(car => {
    updatePagination(car);
  });
}

function renderCard(hero, idx) {
  const id        = String(hero.id || '');
  const name      = esc(hero.displayName || 'Hero');
  const handle    = esc(hero.handle || '');
  const votes     = Number(hero.votesCast || 0);
  const goal      = Number(hero.voteGoal || hero.goalVotes || Math.max(votes * 2, 5000));
  const accent    = HERO_ACCENTS[idx % HERO_ACCENTS.length];
  const photo     = hero.image || hero.imageUrl || '';
  const initials  = (hero.displayName || '?').split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase();
  const fileNum   = String(hero.fileNo || idx + 1).padStart(3, '0');
  const share     = goal > 0 ? Math.min(100, (votes / goal) * 100) : 0;
  const allocated = state.allocations[id] || 0;
  const isAlloc   = allocated > 0;

  const artStyle = photo
    ? `style="--accent:${accent}; background-image:url('${esc(photo)}');" `
    : `style="--accent:${accent};" `;
  const artClass = photo ? 'ff-hero-card__art ff-hero-card__art--photo' : 'ff-hero-card__art';
  const monoBlock = photo ? '' : `
    <div class="ff-hero-card__grid"></div>
    <div class="ff-hero-card__mono"><div class="ff-hero-card__ring"><span>${initials}</span></div></div>`;

  return `
<div class="ff-hero-card${isAlloc ? ' is-allocated' : ''}" data-hero-id="${esc(id)}">
  <div class="ff-hero-card__badge">
    <span class="num">#${fileNum}</span>
    <span class="pipe"></span>
    <span class="lbl">Nom</span>
  </div>
  <span class="ff-hero-card__share">${share.toFixed(1)}%</span>
  <div class="ff-hero-card__portrait">
    <div class="${artClass}" ${artStyle}></div>
    ${monoBlock}
    <div class="ff-hero-card__name">
      ${name}
      ${handle ? `<small>${handle}</small>` : ''}
    </div>
  </div>
  <div class="ff-hero-card__progress">
    <div class="ff-hero-card__progress-row">
      <span>Votes</span>
      <span class="v">${votes.toLocaleString()}</span>
    </div>
    <div class="ff-hero-card__progress-track">
      <div class="ff-hero-card__progress-fill" style="width:${share}%"></div>
    </div>
  </div>
  <div class="ff-hero-card__alloc-row">
    <div class="ff-vote-stepper" data-hid="${esc(id)}">
      <button type="button" data-act="dec" ${allocated === 0 ? 'disabled' : ''} aria-label="Decrease vote for ${name}">−</button>
      <span class="v ${allocated > 0 ? 'has' : ''}">${allocated === 0 ? '+ Vote' : `+${allocated}`}</span>
      <button type="button" data-act="inc" aria-label="Increase vote for ${name}">+</button>
    </div>
  </div>
</div>`.trim();
}

/* ── Public API: open drawer + scroll a specific hero into view ── */
function focusHero(heroId) {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
  } else {
    // Fallback: try a known cart toggle if the custom element isn't ready yet
    document.querySelector('[data-cart-drawer-toggle], a[href="/cart"]')?.click();
  }

  // Poll for the hero card; the drawer hydrates async after fetchHeroes()
  let tries = 0;
  const target = String(heroId);
  const timer = setInterval(() => {
    tries += 1;
    const card = document.querySelector(`.ff-hero-card[data-hero-id="${cssEscape(target)}"]`);
    if (card) {
      clearInterval(timer);
      const carousel = card.closest('[data-ff-carousel]');
      if (carousel) {
        const left = card.offsetLeft - (carousel.clientWidth - card.offsetWidth) / 2;
        carousel.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      } else {
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      card.classList.add('is-focused');
      setTimeout(() => card.classList.remove('is-focused'), 2400);
    } else if (tries > 40) {
      clearInterval(timer);
    }
  }, 100);
}

if (typeof window !== 'undefined') {
  window.FFVoting = Object.assign(window.FFVoting || {}, { focusHero });
}

/* ── Allocation logic ── */
function incAllocation(heroId) {
  const remaining = state.earnedVotes - totalAllocated();
  if (remaining <= 0) return;
  state.allocations[heroId] = (state.allocations[heroId] || 0) + 1;
  persistAllocations();
  patchCard(heroId);
  renderAllocPanel();
}

function decAllocation(heroId) {
  const cur = state.allocations[heroId] || 0;
  if (cur <= 0) return;
  if (cur - 1 === 0) delete state.allocations[heroId];
  else state.allocations[heroId] = cur - 1;
  persistAllocations();
  patchCard(heroId);
  renderAllocPanel();
}

function splitEqually() {
  // Distribute the full earned pool as evenly as possible across all heroes.
  // Largest-remainder: everyone gets floor(votes/heroes); the leftover is handed
  // out one-at-a-time to the leading heroes. With fewer votes than heroes, the
  // first `earnedVotes` heroes get one each.
  const heroes = state.heroes;
  if (!heroes.length || state.earnedVotes <= 0) return;

  const base = Math.floor(state.earnedVotes / heroes.length);
  let remainder = state.earnedVotes - base * heroes.length;

  const next = {};
  heroes.forEach(hero => {
    const id = String(hero.id || '');
    if (!id) return;
    const count = base + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder -= 1;
    if (count > 0) next[id] = count;
  });

  state.allocations = next;
  persistAllocations();
  renderCarousels();
  renderAllocPanel();
}

function totalAllocated() {
  return Object.values(state.allocations).reduce((s, n) => s + n, 0);
}

function clampAllocations() {
  // If cart shrank, scale down allocations proportionally (or drop overflow)
  const total = totalAllocated();
  if (total <= state.earnedVotes) return;
  if (state.earnedVotes === 0) {
    state.allocations = {};
    persistAllocations();
    return;
  }
  // Trim from any hero until within budget
  const ids = Object.keys(state.allocations);
  let overflow = total - state.earnedVotes;
  while (overflow > 0 && ids.length) {
    for (const id of ids) {
      if (overflow <= 0) break;
      if ((state.allocations[id] || 0) > 0) {
        state.allocations[id] -= 1;
        overflow -= 1;
        if (state.allocations[id] === 0) delete state.allocations[id];
      }
    }
  }
  persistAllocations();
}

function patchCard(heroId) {
  // Surgical DOM update instead of full re-render so scroll position is preserved
  document.querySelectorAll(`.ff-hero-card[data-hero-id="${cssEscape(heroId)}"]`).forEach(card => {
    const allocated = state.allocations[heroId] || 0;
    card.classList.toggle('is-allocated', allocated > 0);
    const stepper = card.querySelector('.ff-vote-stepper');
    if (!stepper) return;
    const v   = stepper.querySelector('.v');
    const dec = stepper.querySelector('[data-act="dec"]');
    if (v) {
      v.textContent = allocated === 0 ? '+ Vote' : `+${allocated}`;
      v.classList.toggle('has', allocated > 0);
    }
    if (dec) dec.disabled = allocated === 0;
  });
}

function renderAllocPanel() {
  const committed = totalAllocated();
  const remaining = Math.max(0, state.earnedVotes - committed);
  const pct       = state.earnedVotes > 0 ? (committed / state.earnedVotes) * 100 : 0;

  document.querySelectorAll('[data-ff-alloc-committed]').forEach(el => el.textContent = committed);
  document.querySelectorAll('[data-ff-alloc-remaining]').forEach(el => el.textContent = remaining);
  document.querySelectorAll('[data-ff-alloc-meter]').forEach(el => el.style.width = `${pct}%`);
  document.querySelectorAll('[data-ff-earned-votes]').forEach(el => el.textContent = state.earnedVotes);

  // Update the "/ N" suffix to reflect current earned pool
  document.querySelectorAll('.ff-vc-alloc__num span:last-child').forEach(el => {
    el.textContent = `/ ${state.earnedVotes}`;
  });
}

function persistAllocations() {
  try {
    localStorage.setItem(LS_KEY_ALLOCATIONS, JSON.stringify(state.allocations));
  } catch (_) {}

  // Debounce writes to cart attributes
  clearTimeout(persistAllocations._t);
  persistAllocations._t = setTimeout(() => {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { [CART_ATTR_ALLOCATIONS]: JSON.stringify(state.allocations) },
      }),
    }).catch(err => console.warn('[ffvc] cart attr write failed', err));
  }, 350);
}

function restoreAllocations() {
  try {
    const raw = localStorage.getItem(LS_KEY_ALLOCATIONS);
    if (raw) state.allocations = JSON.parse(raw) || {};
  } catch (_) {
    state.allocations = {};
  }
}

/* ── Pagination dots ── */
function updatePagination(car) {
  const root = car.closest('[data-ff-vote-commit]');
  const dots = root?.querySelector('[data-ff-pagination]');
  if (!dots) return;

  const cardW = car.querySelector('.ff-hero-card')?.offsetWidth || 200;
  const gap   = 12;
  const step  = cardW + gap;
  const visibleCards = Math.max(1, Math.floor(car.clientWidth / step));
  const totalCards   = car.querySelectorAll('.ff-hero-card').length;
  const pages = Math.max(1, Math.ceil(totalCards / visibleCards));
  const active = Math.min(pages - 1, Math.round(car.scrollLeft / (step * visibleCards)));

  let html = '';
  for (let i = 0; i < pages; i++) {
    html += `<span class="ff-vc-pagination__dot${i === active ? ' is-active' : ''}"></span>`;
  }
  dots.innerHTML = html;
}

/* ── Shipping bar ── */
function updateShipBars() {
  document.querySelectorAll('[data-ff-shipbar]').forEach(bar => {
    const threshold = parseInt(bar.dataset.threshold || 3800, 10);
    const total     = parseInt(bar.dataset.cartTotal  || 0, 10);
    const remaining = Math.max(0, threshold - total);
    const pct       = Math.min(100, Math.round((total / threshold) * 100));
    const unlocked  = total >= threshold;

    const msg  = bar.querySelector('[data-ff-shipbar-msg]');
    const togo = bar.querySelector('[data-ff-shipbar-togo]');
    const fill = bar.querySelector('[data-ff-shipbar-fill]');

    if (msg) {
      msg.textContent = unlocked
        ? 'Free Shipping Unlocked'
        : `${formatMoney(remaining)} To Free Shipping`;
    }

    if (togo) {
      togo.innerHTML = unlocked
        ? `${formatMoney(total)}<small>Earned</small>`
        : `${formatMoney(remaining)}<small>To Go</small>`;
    }

    if (fill) fill.style.width = `${pct}%`;
    bar.classList.toggle('is-unlocked', unlocked);

    bar.querySelectorAll('[data-ff-tick]').forEach(tick => {
      const at = parseInt(tick.dataset.ffTick || 0, 10);
      tick.classList.toggle('is-reached', total >= at);
    });
  });
}

/* ── Puck bar ── */
function updatePuckBars() {
  document.querySelectorAll('[data-ff-puckbar]').forEach(bar => {
    const threshold = parseInt(bar.dataset.puckThreshold || 3, 10);
    const qty       = parseInt(bar.dataset.puckQty || 0, 10);
    const remaining = Math.max(0, threshold - qty);
    const pct       = Math.min(100, Math.round((qty / threshold) * 100));
    const unlocked  = qty >= threshold;

    const msg  = bar.querySelector('[data-ff-puckbar-msg]');
    const togo = bar.querySelector('[data-ff-puckbar-togo]');
    const fill = bar.querySelector('[data-ff-puckbar-fill]');

    if (msg) {
      msg.textContent = unlocked
        ? 'Free Puck · Unlocked'
        : remaining === 1 ? '1 More Puck To Unlock' : `${remaining} More Pucks To Unlock`;
    }
    if (togo) {
      togo.innerHTML = unlocked
        ? 'FREE<small>No Code Needed</small>'
        : `+${remaining}<small>To Unlock</small>`;
    }
    if (fill) fill.style.width = `${pct}%`;
    bar.classList.toggle('is-unlocked', unlocked);
  });
}

/* ── Helpers ── */
function setCarousels(html) {
  document.querySelectorAll('[data-ff-carousel]').forEach(el => {
    el.innerHTML = html;
  });
}

function loadingHTML() {
  return '<div class="ff-vc-loading"><div><span></span><span></span><span></span></div></div>';
}

function emptyHTML(msg) {
  return `<div class="ff-vc-empty">${esc(msg)}</div>`;
}

function formatMoney(cents) {
  const dollars = (cents / 100).toFixed(2).replace(/\.00$/, '');
  return `$${dollars}`;
}

function esc(str) {
  return String(str == null ? '' : str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function cssEscape(str) {
  return String(str).replace(/(["\\\]\[])/g, '\\$1');
}
